import LOCALS from 'commons/locals';
import { CreateProductFormSchemaType, useGenerateDetailTitle } from '../common';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { FormLabel } from '../form-label';
import i18n from 'i18n';
import { Select } from 'antd';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import { SelectOption } from 'types/base';
import { useCallback, useMemo } from 'react';

const ProductRank = ({
  control,
  errors,
  setValue,
}: {
  control: Control<CreateProductFormSchemaType>;
  errors: FieldErrors<CreateProductFormSchemaType>;
  setValue: UseFormSetValue<CreateProductFormSchemaType>;
}) => {
  const { rankSelectOptions } = useAppSelector(selectGlobalInfo);

  const rankListSort = useMemo(() => {
    if (rankSelectOptions && rankSelectOptions.length) {
      return [...rankSelectOptions].sort((a, b) => b.sort! - a.sort!);
    }
    return [];
  }, [rankSelectOptions]);

  const { generateDetailTitle } = useGenerateDetailTitle(control);

  const rankDesc = useWatch({
    control,
    name: 'rankDesc',
  });

  const firstLineRank = useCallback(
    (rankDesc: string, newRankLabel: string) => {
      const lines = rankDesc.split('\n');
      const firstLine = lines[0];

      // 使用rankListSort来检查第一行是否包含任何已定义的rank标签
      const isRankLine = rankListSort.some((option) => {
        return (
          (option.labelEn && firstLine.includes(option.labelEn)) ||
          (option.labelJa && firstLine.includes(option.labelJa)) ||
          (option.labelCn && firstLine.includes(option.labelCn)) ||
          (option.labelTw && firstLine.includes(option.labelTw))
        );
      });

      if (isRankLine) {
        lines[0] = newRankLabel;
      } else {
        lines.unshift(newRankLabel);
      }

      return lines.join('\n');
    },
    [rankListSort]
  );

  const SelectFilter = useCallback(
    (input: string, option: SelectOption | undefined) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    []
  );

  return (
    <div className="flex items-center gap-2">
      <FormLabel required>{i18n.t(LOCALS.rank)}:</FormLabel>
      <Controller
        name="rank"
        control={control}
        render={({ field }) => (
          <div className="relative w-full">
            <Select
              status={errors.rank ? 'error' : undefined}
              {...field}
              options={rankListSort}
              className="w-full"
              placeholder={i18n.t(LOCALS.please_select) || ''}
              filterOption={SelectFilter}
              showSearch
              onChange={(value) => {
                field.onChange(value);
                const detailTitle = generateDetailTitle({
                  rank: value,
                });
                setValue('detailTitle', detailTitle);

                const targetRank = rankListSort.find((d) => d.value === value);
                if (targetRank) {
                  const newEnRankDesc = firstLineRank(
                    rankDesc?.en || '',
                    targetRank.labelEn || ''
                  );
                  const newJaRankDesc = firstLineRank(
                    rankDesc?.ja || '',
                    targetRank.labelJa || ''
                  );
                  const newZhCnRankDesc = firstLineRank(
                    rankDesc?.zh_CN || '',
                    targetRank.labelCn || ''
                  );
                  const newZhTwRankDesc = firstLineRank(
                    rankDesc?.zh_TW || '',
                    targetRank.labelTw || ''
                  );
                  setValue('rankDesc', {
                    en: newEnRankDesc,
                    ja: newJaRankDesc,
                    zh_CN: newZhCnRankDesc,
                    zh_TW: newZhTwRankDesc,
                  });
                }
              }}
            />
            {errors.rank && (
              <p style={{ color: 'red' }} className="absolute left-0">
                {i18n.t(LOCALS.required_field)}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default ProductRank;
